import triggerTracking from '@blueprint/tracking';
import {
    Maybe,
    PropertyDetails,
    TrackedPropertyRelationship
} from 'graphql.generated';
import { TRACKING_EVENT, TRACKING_CATEGORY } from 'my-account/consts';

export type TrackedPropertyTracking = {
    taxonomy: {
        property_relationship?: string | null;
        property_id?: Maybe<string>;
        uprn?: Maybe<string>;
        price?: Maybe<number>;
        outcode?: Maybe<string>;
        location?: Maybe<string>;
    };
};

type TrackingPropertyData = {
    details?: Maybe<PropertyDetails>;
    uprn?: Maybe<string>;
    propertyId?: string;
};

export const trackingMap: Record<TrackedPropertyRelationship, string> = {
    [TrackedPropertyRelationship.Homeowner]: 'homeowner',
    [TrackedPropertyRelationship.Landlord]: 'landlord',
    [TrackedPropertyRelationship.Tenant]: 'tenant',
    [TrackedPropertyRelationship.InterestedInBuying]: 'interested_in_buying',
    [TrackedPropertyRelationship.Interested]: 'interested'
};

export const getTrackedPropertyTaxonomyData = (
    { uprn, propertyId, details }: TrackingPropertyData,
    relationship?: Maybe<TrackedPropertyRelationship>
) => {
    const propertyType = details?.tags.filter(
        (tag: string) =>
            tag !== 'freehold' &&
            tag !== 'leasehold' &&
            tag !== 'shareOfFreehold'
    );

    return {
        uprn,
        property_id: propertyId,
        price: details?.estimate?.sale?.currentPrice,
        outcode: details?.outcode,
        location: details?.postcode,
        property_relationship:
            (relationship && trackingMap[relationship]) || 'undefined',
        postal_area: details?.postalArea,
        num_baths: details?.bathrooms || null,
        num_beds: details?.bedrooms || null,
        num_recepts: details?.livingRooms || null,
        property_type: propertyType?.length ? propertyType : null
    };
};

export const trackPropertyCard = (
    trackingData: TrackingPropertyData,
    relationship?: Maybe<TrackedPropertyRelationship>
) => {
    const taxonomy = getTrackedPropertyTaxonomyData(trackingData, relationship);
    switch (relationship) {
        case TrackedPropertyRelationship.Homeowner:
            return triggerTracking<TrackedPropertyTracking>({
                event: TRACKING_EVENT,
                eventCategory: TRACKING_CATEGORY,
                eventAction: 'clickthrough',
                eventLabel: 'saved property>homeowner',
                taxonomy
            });
        case TrackedPropertyRelationship.Landlord:
            return triggerTracking<TrackedPropertyTracking>({
                event: TRACKING_EVENT,
                eventCategory: TRACKING_CATEGORY,
                eventAction: 'clickthrough',
                eventLabel: 'saved property>landlord',
                taxonomy
            });
        default:
            triggerTracking<TrackedPropertyTracking>({
                event: TRACKING_EVENT,
                eventCategory: TRACKING_CATEGORY,
                eventAction: 'clickthrough',
                eventLabel: 'saved property',
                taxonomy
            });
    }
};

export const trackPropertyRemove = (
    trackingData: TrackingPropertyData,
    relationship?: TrackedPropertyRelationship | null
) => {
    const taxonomy = getTrackedPropertyTaxonomyData(trackingData, relationship);
    triggerTracking<TrackedPropertyTracking>({
        event: TRACKING_EVENT,
        eventCategory: 'property details',
        eventAction: 'remove',
        eventLabel: 'save property',
        taxonomy
    });
};

export const trackEditedRelationship = (
    existing: TrackedPropertyRelationship | undefined | null,
    updated: TrackedPropertyRelationship,
    trackingData: TrackingPropertyData
) => {
    const existingRelationship = existing ? trackingMap[existing] : 'undefined';
    const newRelationship = updated ? trackingMap[updated] : 'undefined';
    const taxonomy = getTrackedPropertyTaxonomyData(
        trackingData,
        updated || existing
    );

    triggerTracking<TrackedPropertyTracking>({
        event: TRACKING_EVENT,
        eventCategory: 'track property relationship',
        eventAction: 'save',
        eventLabel: `edit relationship to ${newRelationship} from ${existingRelationship}`,
        taxonomy
    });
};

export const trackRelationshipModalClose = (
    trackingData: TrackingPropertyData,
    currentRelationship?: TrackedPropertyRelationship | null | undefined
) => {
    const taxonomy = getTrackedPropertyTaxonomyData(
        trackingData,
        currentRelationship
    );
    const relationship = currentRelationship
        ? trackingMap[currentRelationship]
        : 'undefined';
    triggerTracking<TrackedPropertyTracking>({
        event: TRACKING_EVENT,
        eventCategory: 'track property relationship',
        eventAction: 'form closed',
        eventLabel: `edit relationship ${relationship}`,
        taxonomy
    });
};

export const trackPropertyShare = (
    trackingData: TrackingPropertyData,
    currentRelationship?: TrackedPropertyRelationship | null | undefined
) => {
    const taxonomy = getTrackedPropertyTaxonomyData(
        trackingData,
        currentRelationship
    );
    triggerTracking<TrackedPropertyTracking>({
        event: TRACKING_EVENT,
        eventCategory: TRACKING_CATEGORY,
        eventAction: 'click',
        eventLabel: 'open saved property share modal',
        taxonomy
    });
};

export const trackPropertyMobileMenu = (
    type: 'open' | 'close',
    trackingData: TrackingPropertyData,
    currentRelationship?: TrackedPropertyRelationship | null | undefined
) => {
    const taxonomy = getTrackedPropertyTaxonomyData(
        trackingData,
        currentRelationship
    );
    triggerTracking<TrackedPropertyTracking>({
        event: TRACKING_EVENT,
        eventCategory: TRACKING_CATEGORY,
        eventAction: 'click',
        eventLabel: `${type} saved property menu modal`,
        taxonomy
    });
};

export const trackCloseSharingModal = (
    trackingData: TrackingPropertyData,
    currentRelationship?: TrackedPropertyRelationship | null | undefined
) => {
    const taxonomy = getTrackedPropertyTaxonomyData(
        trackingData,
        currentRelationship
    );
    triggerTracking<TrackedPropertyTracking>({
        event: TRACKING_EVENT,
        eventCategory: TRACKING_CATEGORY,
        eventAction: 'click',
        eventLabel: 'close saved property share modal',
        taxonomy
    });
};

export const trackUnmapModal = (
    trackingData: TrackingPropertyData,
    currentRelationship?: TrackedPropertyRelationship | null | undefined
) => {
    const taxonomy = getTrackedPropertyTaxonomyData(
        trackingData,
        currentRelationship
    );
    triggerTracking<TrackedPropertyTracking>({
        event: TRACKING_EVENT,
        eventCategory: TRACKING_CATEGORY,
        eventAction: 'click',
        eventLabel: 'unmapped_property_info',
        taxonomy
    });
};
